import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { DEFAULT_TERMINOLOGY_SECTOR } from '../constants/terminology';
import { LayoutService } from '../services/layout/layout';
import { TerminologyService } from '../services/terminology/terminology';
import { TerminologyDictionary } from '../models/terminology.interface';

/**
 * Registers terminology bootstrap:
 * 1. Loads `assets/terminology/default.json` before the first render.
 * 2. Reloads the catalog whenever `LayoutService` changes the active sector.
 *
 * @example app bootstrap (`main.ts`)
 * ```ts
 * bootstrapApplication(App, {
 *   providers: [
 *     provideHttpClient(withInterceptors([...])),
 *     provideTerminology(),
 *     provideRouter(routes),
 *   ],
 * });
 * ```
 *
 * @example template
 * ```html
 * <h1>Lista de {{ 'client.plural' | term }}</h1>
 * <p>{{ 'welcome' | term:{ name: profile.first_name } }}</p>
 * <button>Crear {{ 'client.singular' | term:'Cliente' }}</button>
 * ```
 */
export function provideTerminology(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(initializeTerminology),
  ]);
}

export function initializeTerminology(): Promise<TerminologyDictionary> {
  const terminology = inject(TerminologyService);
  const layout = inject(LayoutService);

  layout.sectorSelected$.subscribe((sector) => {
    if (!sector?.name) {
      return;
    }

    void terminology.loadSectorTerms(sector.name);
  });

  return terminology.loadSectorTerms(DEFAULT_TERMINOLOGY_SECTOR).then((result) => result.terms);
}
